import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import {
  ATTENDANCE_MODE_OPTIONS,
  attendanceModeLabel,
  createWorkPolicy,
  deleteWorkPolicy,
  fetchAttendanceRulesCatalog,
  fetchAttendanceRulesSetup,
  listWorkPolicies,
  syncWorkPoliciesFromApi,
  syncWorkPolicyAttendanceRules,
  updateWorkPolicy,
  workPolicyApiErrorMessage,
  type AttendanceCatalogItem,
  type AttendanceMode,
  type WorkPolicyDraft,
  type WorkPolicyLinkedRuleApi,
  type WorkPolicyRecord,
} from "@/lib/hr/workPoliciesStore";
import { type AttendanceRuleCategory, ATTENDANCE_RULE_TYPE_LABELS } from "@/lib/hr/attendanceStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  CalendarIcon,
  CheckCircle2,
  Coffee,
  Edit,
  FileCheck2,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

const emptyDraft = (): WorkPolicyDraft => ({
  name: "",
  attendanceMode: "shift",
  isActive: true,
});

type PolicySortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

type RuleRowState = {
  code: string;
  title: string;
  description: string;
  type: AttendanceRuleCategory;
  checked: boolean;
  valueStr: string;
  boolVal: boolean;
  dataType: string;
};

function stripQuotes(v: string): string {
  const trimmed = v.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function formatGlobalScalar(v: unknown): string {
  if (typeof v === "boolean") return v ? "true" : "false";
  if (v === null || v === undefined) return "";
  return stripQuotes(String(v));
}

function buildRuleRows(catalog: AttendanceCatalogItem[], linked: WorkPolicyLinkedRuleApi[]): RuleRowState[] {
  const linkedByCode = new Map(linked.map((l) => [String(l.rule_key ?? ""), l]));
  return catalog.map((c) => {
    const l = linkedByCode.get(c.code);
    const dt = String(l?.data_type || c.data_type || "string");
    const rawFromLink = l ? String(l.rule_value ?? "") : formatGlobalScalar(c.global_value);
    const fromLink = stripQuotes(rawFromLink);
    const boolVal = fromLink === "true" || fromLink === "1" || fromLink === "yes";
    return {
      code: c.code,
      title: c.title,
      description: c.description || String(l?.description ?? ""),
      type: c.type,
      checked: Boolean(l),
      valueStr: dt === "boolean" ? (boolVal ? "true" : "false") : (c.code === "multi_punch_mode" && !fromLink ? "sum_pairs" : c.code === "short_time_calculation_mode" && !fromLink ? "shift_window" : fromLink),
      boolVal: dt === "boolean" ? boolVal : false,
      dataType: dt,
    };
  });
}

function matchesAttendanceRuleSearch(r: RuleRowState, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return (
    r.title.toLowerCase().includes(n) ||
    r.code.toLowerCase().includes(n) ||
    r.description.toLowerCase().includes(n) ||
    ATTENDANCE_RULE_TYPE_LABELS[r.type]?.toLowerCase().includes(n)
  );
}

function attendanceRulesFooter(p: WorkPolicyRecord): string {
  const n = p.attendanceRules.length;
  if (n === 0) return "No rules yet";
  const preview = p.attendanceRules
    .slice(0, 2)
    .map((r) => `${r.ruleKey}=${r.ruleValue}`)
    .join("; ");
  return `${n} rule(s) · ${preview}${n > 2 ? "…" : ""}`;
}

export default function WorkPolicies() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "work_policy_can_create");
  const canUpdate = can(user, "work_policy_can_update");
  const canDelete = can(user, "work_policy_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<PolicySortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");

  const [editing, setEditing] = useState<WorkPolicyRecord | null>(null);
  const [draft, setDraft] = useState<WorkPolicyDraft>(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState<WorkPolicyRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [policyEditorOpen, setPolicyEditorOpen] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [ruleRows, setRuleRows] = useState<RuleRowState[]>([]);
  const [availableRulesQuery, setAvailableRulesQuery] = useState("");
  const [selectedRulesQuery, setSelectedRulesQuery] = useState("");
  const [ruleFeedback, setRuleFeedback] = useState<{ type: "success" | "error"; message: string; id: number } | null>(null);

  const policies = useMemo(() => listWorkPolicies(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncWorkPoliciesFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[WorkPolicies] Load failed", error);
        if (mounted) toast.error("Could not fetch work policies. Showing cached data if any.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();

    return policies.filter((p) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(p.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(p.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (nameNeedle && !p.name.toLowerCase().includes(nameNeedle)) return false;
      if (filterActive === "active" && !p.isActive) return false;
      if (filterActive === "inactive" && p.isActive) return false;
      const matchesQuery = !q || p.name.toLowerCase().includes(q);
      return matchesQuery;
    });
  }, [policies, query, filterCreatedDate, quickDate, filterName, filterActive]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) + (filterName.trim() ? 1 : 0) + (filterActive !== "all" ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterActive("all");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterActive}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const closePolicyEditor = () => {
    setPolicyEditorOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
    setRuleRows([]);
    setAttendanceLoading(false);
    setAvailableRulesQuery("");
    setSelectedRulesQuery("");
    setRuleFeedback(null);
  };

  const openCreate = () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create work policies.");
      return;
    }
    setEditing(null);
    setDraft(emptyDraft());
    setRuleRows([]);
    setAvailableRulesQuery("");
    setSelectedRulesQuery("");
    setRuleFeedback(null);
    setPolicyEditorOpen(true);
    void (async () => {
      setAttendanceLoading(true);
      try {
        const { catalog, linked } = await fetchAttendanceRulesCatalog();
        setRuleRows(buildRuleRows(catalog, linked));
      } catch (error) {
        console.error("[WorkPolicies] Attendance catalog failed", error);
        toast.error(workPolicyApiErrorMessage(error));
        closePolicyEditor();
      } finally {
        setAttendanceLoading(false);
      }
    })();
  };

  const openEdit = (p: WorkPolicyRecord) => {
    if (!canUpdate) {
      toast.error("You don’t have permission to update work policies.");
      return;
    }
    setEditing(p);
    setDraft({
      name: p.name,
      attendanceMode: p.attendanceMode,
      isActive: p.isActive,
    });
    setRuleRows([]);
    setAvailableRulesQuery("");
    setSelectedRulesQuery("");
    setRuleFeedback(null);
    setPolicyEditorOpen(true);
    void (async () => {
      setAttendanceLoading(true);
      try {
        const { catalog, linked } = await fetchAttendanceRulesSetup(p.id);
        setRuleRows(buildRuleRows(catalog, linked));
      } catch (error) {
        console.error("[WorkPolicies] Attendance setup failed", error);
        toast.error(workPolicyApiErrorMessage(error));
        closePolicyEditor();
      } finally {
        setAttendanceLoading(false);
      }
    })();
  };

  const validateAttendanceRuleRows = (rows: RuleRowState[]): string | null => {
    const selected = rows.filter((r) => r.checked);
    for (const r of selected) {
      if (r.dataType === "boolean") continue;
      if (r.dataType === "int") {
        if (!/^-?\d+$/.test(r.valueStr.trim())) {
          return `"${r.title}" needs a whole number.`;
        }
      }
      if (r.dataType === "decimal") {
        if (!/^-?\d+(\.\d+)?$/.test(r.valueStr.trim())) {
          return `"${r.title}" needs a valid decimal.`;
        }
      }
    }
    return null;
  };

  const savePolicyEditor = async () => {
    if (isSaving) return;
    if (!draft.name.trim()) {
      toast.error("Policy name is required.");
      return;
    }
    const payload: WorkPolicyDraft = {
      name: draft.name.trim(),
      attendanceMode: draft.attendanceMode,
      isActive: draft.isActive,
    };

    setIsSaving(true);
    try {
      const validationError = validateAttendanceRuleRows(ruleRows);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const selected = ruleRows.filter((r) => r.checked);
      const rules = selected.map((r) => {
        const rule_value =
          r.dataType === "boolean" ? (r.boolVal ? "true" : "false") : r.valueStr.trim().slice(0, 255);
        return {
          rule_key: r.code,
          rule_value,
          data_type: r.dataType as "int" | "boolean" | "string" | "decimal",
          description: r.description || r.title,
        };
      });

      if (!editing) {
        const result = await createWorkPolicy(payload);
        const { message } = await syncWorkPolicyAttendanceRules(result.record.id, rules);
        toast.success(message || result.message);
        closePolicyEditor();
        refresh();
        return;
      }

      const updateResult = await updateWorkPolicy(editing.id, payload);
      if (!updateResult) {
        toast.error("Work policy not found.");
        return;
      }

      const { message } = await syncWorkPolicyAttendanceRules(updateResult.record.id, rules);
      toast.success(message);
      closePolicyEditor();
      refresh();
    } catch (error) {
      console.error("[WorkPolicies] Save failed", error);
      toast.error(workPolicyApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    if (!canDelete) {
      toast.error("You don’t have permission to delete work policies.");
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteWorkPolicy(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[WorkPolicies] Delete failed", error);
      toast.error(workPolicyApiErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const updateRuleRow = (code: string, patch: Partial<RuleRowState>) => {
    setRuleRows((rows) => {
      const prev = rows.find((r) => r.code === code);
      if (!prev) return rows;
      const nextRow = { ...prev, ...patch };
      if ("checked" in patch && typeof patch.checked === "boolean" && prev.checked !== patch.checked) {
        const title = prev.title;
        window.setTimeout(() => {
          setRuleFeedback({
            type: patch.checked ? "success" : "error",
            message: patch.checked
              ? `“${title}” was added to selected rules.`
              : `“${title}” was removed from selected rules.`,
            id: Date.now(),
          });
        }, 0);
      }
      return rows.map((r) => (r.code === code ? nextRow : r));
    });
  };

  useEffect(() => {
    if (!ruleFeedback) return;
    const t = window.setTimeout(() => setRuleFeedback(null), 4200);
    return () => window.clearTimeout(t);
  }, [ruleFeedback]);

  const includedRuleRows = useMemo(() => ruleRows.filter((r) => r.checked), [ruleRows]);
  const addMoreRuleRows = useMemo(() => ruleRows.filter((r) => !r.checked), [ruleRows]);

  const filteredAvailableRules = useMemo(
    () => addMoreRuleRows.filter((r) => matchesAttendanceRuleSearch(r, availableRulesQuery)),
    [addMoreRuleRows, availableRulesQuery],
  );
  const filteredSelectedRules = useMemo(
    () => includedRuleRows.filter((r) => matchesAttendanceRuleSearch(r, selectedRulesQuery)),
    [includedRuleRows, selectedRulesQuery],
  );

  const renderRuleTypeBadge = (type: AttendanceRuleCategory) => (
    <span
      className={cn(
        "inline-flex shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none",
        type === "attendance"
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-violet-500/25 bg-violet-500/10 text-violet-800 dark:text-violet-200",
      )}
    >
      {ATTENDANCE_RULE_TYPE_LABELS[type]}
    </span>
  );

  const renderAvailableRuleRow = (r: RuleRowState) => (
    <div
      key={r.code}
      className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2"
    >
      <Checkbox
        checked={r.checked}
        onCheckedChange={(c) => updateRuleRow(r.code, { checked: c === true })}
        className="mt-0.5"
        id={`rule-av-${r.code}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1.5">
          <Label htmlFor={`rule-av-${r.code}`} className="text-xs font-medium text-foreground cursor-pointer leading-snug line-clamp-2">
            {r.title}
          </Label>
          {renderRuleTypeBadge(r.type)}
        </div>
        {r.description ? (
          <p className="mt-0.5 text-[10px] text-muted-foreground leading-snug line-clamp-2" title={r.description}>
            {r.description}
          </p>
        ) : null}
        <code className="mt-0.5 block text-[10px] text-muted-foreground font-mono truncate">{r.code}</code>
      </div>
    </div>
  );

  const renderSelectedRuleRow = (r: RuleRowState) => (
    <div key={r.code} className="flex flex-col gap-2 rounded-md border border-border/50 bg-background/80 p-2">
      <div className="flex items-start gap-2 min-w-0">
        <Checkbox
          checked={r.checked}
          onCheckedChange={(c) => updateRuleRow(r.code, { checked: c === true })}
          className="mt-0.5"
          id={`rule-sel-${r.code}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5 flex-wrap">
            <Label htmlFor={`rule-sel-${r.code}`} className="text-xs font-medium text-foreground cursor-pointer leading-snug">
              {r.title}
            </Label>
            {renderRuleTypeBadge(r.type)}
          </div>
          {r.description ? (
            <p className="mt-0.5 text-[10px] text-muted-foreground leading-snug line-clamp-2" title={r.description}>
              {r.description}
            </p>
          ) : null}
          <code className="mt-0.5 block text-[10px] text-muted-foreground font-mono truncate">{r.code}</code>
        </div>
      </div>
      <div className="w-full pl-6">
        {r.code === "multi_punch_mode" ? (
          <Select
            value={r.valueStr === "first_last" ? "first_last" : "sum_pairs"}
            onValueChange={(v) => updateRuleRow(r.code, { valueStr: v })}
          >
            <SelectTrigger className="glass-input h-8 text-xs">
              <SelectValue placeholder="Select calculation mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sum_pairs">Sum punch pairs (e.g. 1st IN-OUT + 2nd IN-OUT)</SelectItem>
              <SelectItem value="first_last">First IN to Last OUT (Continuous span)</SelectItem>
            </SelectContent>
          </Select>
        ) : r.code === "short_time_calculation_mode" ? (
          <Select
            value={r.valueStr === "net_daily_hours" || r.valueStr === "require_full_shift_for_ot" ? r.valueStr : "shift_window"}
            onValueChange={(v) => updateRuleRow(r.code, { valueStr: v })}
          >
            <SelectTrigger className="glass-input h-8 text-xs">
              <SelectValue placeholder="Select short time mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="shift_window">Shift window (Deduct OT from shift fulfillment — e.g. SOT 0:52, ST 2:22)</SelectItem>
              <SelectItem value="net_daily_hours">Net daily hours (Compare total worked vs expected — e.g. SOT 0:52, ST 1:30)</SelectItem>
              <SelectItem value="require_full_shift_for_ot">Require full shift for OT (Zero out OT if day is short — e.g. SOT 0:00, ST 1:30)</SelectItem>
            </SelectContent>
          </Select>
        ) : r.dataType === "boolean" ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-2 py-1.5 bg-muted/30">
            <span className="text-[11px] text-muted-foreground">Value</span>
            <Switch
              checked={r.boolVal}
              onCheckedChange={(v) => updateRuleRow(r.code, { boolVal: v, valueStr: v ? "true" : "false" })}
            />
          </div>
        ) : (
          <Input
            className="glass-input h-8 text-xs"
            value={r.valueStr}
            onChange={(e) => updateRuleRow(r.code, { valueStr: e.target.value })}
            placeholder={r.dataType === "int" ? "Integer" : r.dataType === "decimal" ? "Decimal" : "Text"}
          />
        )}
      </div>
    </div>
  );

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Work policies access required"
        message="You don’t have permission to view work policies. Ask an administrator to grant work_policy_can_view_any."
      />
    );
  }

  return (
    <Layout title="Work policies">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Work policies</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Group rules and expectations by policy name (e.g. developer, field staff).{" "}
              <Link to="/leave-requests" className="text-primary hover:underline font-medium">
                Leave requests
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add work policy
            </Button>
          ) : null}
        </div>

        <Dialog
          open={policyEditorOpen}
          onOpenChange={(o) => {
            if (!o) closePolicyEditor();
            else setPolicyEditorOpen(true);
          }}
        >
          <DialogContent className="!flex min-h-0 max-w-4xl w-[96vw] max-h-[90vh] flex-col gap-0 p-0 overflow-hidden sm:max-h-[90vh]">
            <DialogHeader className="px-4 pt-4 pb-1.5 shrink-0">
              <DialogTitle className="text-left text-base">
                {editing ? "Edit work policy" : "New work policy"}
              </DialogTitle>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-1 pb-2 [-webkit-overflow-scrolling:touch]">
                <div className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="space-y-1 flex-1 min-w-0">
                      <Label className="text-xs text-muted-foreground">
                        Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        className="glass-input h-9"
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        placeholder="Developer Policy"
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-1 flex-1 min-w-0">
                      <Label className="text-xs text-muted-foreground">
                        Attendance mode <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={draft.attendanceMode}
                        onValueChange={(v) =>
                          setDraft((d) => ({ ...d, attendanceMode: v as AttendanceMode }))
                        }
                      >
                        <SelectTrigger className="glass-input h-9">
                          <SelectValue placeholder="Select attendance mode" />
                        </SelectTrigger>
                        <SelectContent>
                          {ATTENDANCE_MODE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex h-9 items-center gap-2 sm:shrink-0 sm:pb-0">
                      <Checkbox
                        checked={draft.isActive}
                        onCheckedChange={(c) => setDraft((d) => ({ ...d, isActive: c !== false }))}
                        id="policy-active"
                      />
                      <Label htmlFor="policy-active" className="text-sm font-normal cursor-pointer whitespace-nowrap">
                        Active
                      </Label>
                    </div>
                  </div>
                  {!attendanceLoading && ruleFeedback ? (
                    <div className="relative">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={ruleFeedback.id}
                          role="status"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.6 }}
                          className={cn(
                            "flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs",
                            ruleFeedback.type === "success"
                              ? "border-emerald-500/40 bg-emerald-500/[0.12] text-emerald-950 dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-50"
                              : "border-red-500/45 bg-red-500/[0.12] text-red-950 dark:border-red-400/40 dark:bg-red-500/15 dark:text-red-50",
                          )}
                        >
                          {ruleFeedback.type === "success" ? (
                            <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-600 dark:text-red-400" aria-hidden />
                          )}
                          <span className="leading-snug">{ruleFeedback.message}</span>
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  ) : null}
                  {attendanceLoading ? (
                    <div className="flex justify-center py-10 text-muted-foreground">
                      <Loader2 className="h-7 w-7 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-stretch">
                      <div className="flex min-h-0 flex-col rounded-lg border border-border/60 bg-muted/10 p-2 gap-1.5">
                        <div className="flex items-center justify-between gap-2 shrink-0">
                          <h3 className="text-xs font-semibold text-foreground">Available</h3>
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            {filteredAvailableRules.length}/{addMoreRuleRows.length}
                          </span>
                        </div>
                        <div className="relative shrink-0">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                          <Input
                            className="glass-input h-8 pl-8 text-xs"
                            placeholder="Search available…"
                            value={availableRulesQuery}
                            onChange={(e) => setAvailableRulesQuery(e.target.value)}
                            autoComplete="off"
                          />
                        </div>
                        <div className="min-h-[100px] max-h-[min(52vh,440px)] overflow-y-auto overscroll-contain space-y-1 pr-0.5 [-webkit-overflow-scrolling:touch]">
                          {addMoreRuleRows.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-1.5">All rules are included.</p>
                          ) : filteredAvailableRules.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-1.5">No matches.</p>
                          ) : (
                            filteredAvailableRules.map((r) => renderAvailableRuleRow(r))
                          )}
                        </div>
                      </div>
                      <div className="flex min-h-0 flex-col rounded-lg border border-border/60 bg-muted/10 p-2 gap-1.5">
                        <div className="flex items-center justify-between gap-2 shrink-0">
                          <h3 className="text-xs font-semibold text-foreground">Selected</h3>
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            {filteredSelectedRules.length}/{includedRuleRows.length}
                          </span>
                        </div>
                        <div className="relative shrink-0">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                          <Input
                            className="glass-input h-8 pl-8 text-xs"
                            placeholder="Search selected…"
                            value={selectedRulesQuery}
                            onChange={(e) => setSelectedRulesQuery(e.target.value)}
                            autoComplete="off"
                          />
                        </div>
                        <div className="min-h-[100px] max-h-[min(52vh,440px)] overflow-y-auto overscroll-contain space-y-1 pr-0.5 [-webkit-overflow-scrolling:touch]">
                          {includedRuleRows.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-1.5">Check rules on the left to include them.</p>
                          ) : filteredSelectedRules.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-1.5">No matches.</p>
                          ) : (
                            filteredSelectedRules.map((r) => renderSelectedRuleRow(r))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="px-4 py-3 border-t border-border/60 shrink-0 bg-muted/10">
              <Button type="button" variant="glass" onClick={closePolicyEditor} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="premium"
                onClick={() => void savePolicyEditor()}
                disabled={isSaving || attendanceLoading}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter work policies</SheetTitle>
                  <SheetDescription>Name, status, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Policy</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input className="glass-input h-10" value={filterName} onChange={(e) => setFilterName(e.target.value)} autoComplete="off" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select value={filterActive} onValueChange={(v) => setFilterActive(v as typeof filterActive)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active only</SelectItem>
                          <SelectItem value="inactive">Inactive only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="policy-page-size-mobile" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as PolicySortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="updated_latest">Recently updated</SelectItem>
                  <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="policy-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as PolicySortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading work policies…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {policies.length === 0 ? "No work policies yet. Click “Add work policy” to create one." : "No policies match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  title={p.name}
                  primaryMeta={{
                    label: "Status",
                    value: p.isActive ? "Active" : "Inactive",
                    icon: FileCheck2,
                    iconClassName: p.isActive ? "text-emerald-600" : "text-muted-foreground",
                  }}
                  secondaryMeta={{
                    label: "Attendance mode",
                    value: attendanceModeLabel(p.attendanceMode),
                    icon: Coffee,
                    iconClassName: "text-sky-600",
                  }}
                  footer={attendanceRulesFooter(p)}
                  footerRight={`Updated ${formatCatalogAuditDate(p.updatedAt)}`}
                  onEdit={canUpdate ? () => openEdit(p) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(p) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <FileCheck2 className="w-8 h-8 text-primary/60" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{p.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {p.isActive ? "Active" : "Inactive"} · {attendanceModeLabel(p.attendanceMode)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Updated {formatCatalogAuditDate(p.updatedAt)}</p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-[11px] text-muted-foreground">Created {formatCatalogAuditDate(p.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(p)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(p)}
                        aria-label="Delete policy"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this work policy?"
          description={
            deleteTarget ? (
              <>
                Remove <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}
